import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '@/Hooks/useApi';
import { useBranches, useQueryParams, toDayInput } from '@/Pages/Operations/shared';
import { Select, Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { DELIVERY_ROUTES, type DeliveryAnalyticsSummary } from '../deliveryApi';
import PanelResult from './PanelResult';

/** §16 delivery performance analytics — plain aggregates, gated on `deliveries.view`, the same key the underlying rows already require. */
export default function AnalyticsPanel() {
  const { t } = useTranslation();
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [from, setFrom] = useState(() => toDayInput(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(() => toDayInput(new Date()));

  const params = useQueryParams({
    branchId,
    from: from ? `${from}T00:00:00.000Z` : undefined,
    to: to ? `${to}T23:59:59.999Z` : undefined,
  });
  const summaryQuery = useApi<DeliveryAnalyticsSummary>(DELIVERY_ROUTES.analyticsSummary, params);
  const summary = summaryQuery.data;

  const driverNameFallback = (id: string) => id.slice(-6);

  const byStatusRows = useMemo(
    () => (summary ? Object.entries(summary.byStatus).filter(([, n]) => n > 0) : []),
    [summary],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('delivery.analytics.branch')}
          >
            <option value="">{t('delivery.analytics.allBranches')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        )}
        <Field label={t('delivery.analytics.from')}>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t('delivery.analytics.to')}>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>

      <PanelResult
        loading={summaryQuery.loading && !summaryQuery.data}
        error={summaryQuery.error}
        count={summary ? summary.totalDeliveries : 0}
        onRetry={summaryQuery.reload}
        subject={t('delivery.analytics.subject')}
        emptyTitle={t('delivery.analytics.emptyTitle')}
        emptyHint={t('delivery.analytics.emptyHint')}
      >
        {summary && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Tile label={t('delivery.analytics.total')} value={summary.totalDeliveries} />
              <Tile
                label={t('delivery.analytics.avgAssignToDelivered')}
                value={
                  summary.avgMinutesAssignedToDelivered !== null
                    ? t('delivery.analytics.minutes', { count: summary.avgMinutesAssignedToDelivered })
                    : '—'
                }
              />
              <Tile
                label={t('delivery.analytics.avgPickupToDelivered')}
                value={
                  summary.avgMinutesPickedUpToDelivered !== null
                    ? t('delivery.analytics.minutes', { count: summary.avgMinutesPickedUpToDelivered })
                    : '—'
                }
              />
              <Tile
                label={t('delivery.analytics.failureRate')}
                value={
                  summary.failureRate !== null ? `${Math.round(summary.failureRate * 100)}%` : '—'
                }
                tone={summary.failureRate && summary.failureRate > 0.1 ? 'danger' : undefined}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
              <div className="space-y-2">
                <p className="text-sm font-medium">{t('delivery.analytics.byStatus')}</p>
                {byStatusRows.length === 0 ? (
                  <p className="text-sm text-ink-muted">{t('delivery.analytics.noData')}</p>
                ) : (
                  <TableWrap>
                    <Table>
                      <tbody>
                        {byStatusRows.map(([status, count]) => (
                          <TR key={status}>
                            <TD>{t(`delivery.status.${status}`)}</TD>
                            <TD className="text-end tabular-nums">{count}</TD>
                          </TR>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">{t('delivery.analytics.byDriver')}</p>
                {summary.byDriver.length === 0 ? (
                  <p className="text-sm text-ink-muted">{t('delivery.analytics.noData')}</p>
                ) : (
                  <TableWrap>
                    <Table>
                      <THead>
                        <TR>
                          <TH>{t('delivery.analytics.driver')}</TH>
                          <TH className="text-end">{t('delivery.analytics.delivered')}</TH>
                          <TH className="text-end">{t('delivery.analytics.failed')}</TH>
                        </TR>
                      </THead>
                      <tbody>
                        {summary.byDriver.map((row) => (
                          <TR key={row.driverId}>
                            <TD className="font-mono text-xs">{driverNameFallback(row.driverId)}</TD>
                            <TD className="text-end tabular-nums">{row.delivered}</TD>
                            <TD className="text-end tabular-nums">{row.failed}</TD>
                          </TR>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </div>
            </div>
          </div>
        )}
      </PanelResult>
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: 'danger';
}) {
  return (
    <div className="rounded-xl border bg-surface p-4 shadow-sm">
      <p className="text-sm text-ink-muted">{label}</p>
      <p className={tone === 'danger' ? 'text-2xl font-semibold tabular-nums text-red-600 dark:text-red-400' : 'text-2xl font-semibold tabular-nums'}>
        {value}
      </p>
    </div>
  );
}
